import React from 'react';
import { motion } from 'framer-motion';
import { styles } from '../styles';
import { fadeIn, textVariant } from '../utils/motion';
import { SectionWrapper } from '../hoc';

const principles = [
	{
		title: 'Tradeoffs Over Best Practices',
		description: 'Every architecture decision is a tradeoff. I make them explicit, document them, and revisit when constraints change.',
		icon: (
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
		),
	},
	{
		title: 'Async by Default',
		description: 'Synchronous paths are the bottleneck. I design event-driven flows that decouple producers from consumers and scale independently.',
		icon: (
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
		),
	},
	{
		title: 'Own It End-to-End',
		description: 'I do not hand off designs to implementation teams. I architect it, build it, deploy it, and own the on-call.',
		icon: (
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
		),
	},
	{
		title: 'Complexity is a Cost',
		description: 'I push back on over-engineering. If a cron job solves it, I will not build a distributed scheduler. Simplicity scales better than cleverness.',
		icon: (
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
		),
	},
];

const About = () => {
	return (
		<>
			<motion.div variants={textVariant()}>
				<p className={styles.sectionSubText}>How I Think</p>
				<h2 className={styles.sectionHeadText}>Engineering Philosophy</h2>
			</motion.div>

			<motion.p
				variants={fadeIn('', '', 0.1, 0.4)}
				className="mt-8 text-muted text-[16px] max-w-6xl leading-[30px]">
				I have spent 7+ years in PropTech, building and re-architecting platforms
				that serve millions of residents and process billions in financial
				transactions. My work sits at the intersection of system design,
				production engineering, and team leadership.
			</motion.p>

			<motion.p
				variants={fadeIn('', '', 0.15, 0.4)}
				className="mt-4 text-muted text-[16px] max-w-6xl leading-[30px]">
				I have moved teams from monolithic PHP modules to event-driven services,
				replaced synchronous bottlenecks with async worker pipelines, and built
				AI-assisted operational tooling that reduced manual workflows by 95%.
				I stay close to implementation because architecture decisions that are not
				tested in production are just opinions.
			</motion.p>

			<motion.div
				variants={fadeIn('', '', 0.2, 0.4)}
				className="mt-6 flex flex-wrap gap-3">
				{['Event-Driven Architecture', 'Async Workflow Design', 'AI-Assisted Operations', 'PropTech Domain Expert', 'System Re-architecture'].map((tag) => (
					<span key={tag} className="px-3 py-1.5 bg-accent/5 border border-accent/15 rounded-lg text-accent text-[13px] font-semibold">
						{tag}
					</span>
				))}
			</motion.div>

			<div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-5">
				{principles.map((principle, index) => (
					<motion.div
						key={principle.title}
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.4, delay: index * 0.1 }}
						className="bg-warm border border-border rounded-xl p-6 hover:border-accent/20 transition-colors duration-300">
						<div className="flex items-start gap-4">
							<div className="w-10 h-10 rounded-lg bg-accent/5 border border-accent/10 flex items-center justify-center flex-shrink-0">
								<svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									{principle.icon}
								</svg>
							</div>
							<div>
								<h3 className="text-primary text-[16px] font-bold">{principle.title}</h3>
								<p className="text-muted text-[14px] leading-relaxed mt-1.5">{principle.description}</p>
							</div>
						</div>
					</motion.div>
				))}
			</div>
		</>
	);
};

export default SectionWrapper(About, 'about');
